"use client";

import { useCallback, useEffect, useState } from "react";
import { CartItem } from "./types";

// Shape returned by POST /api/commerce/quotes (owned by the commerce agent).
export interface LiveQuote {
  unitPrice: number | null;
  currency: string;
  available: boolean;
  variantId: string | null;
}

export type QuoteSource = "PLANTISTA" | "SHOPIFY";

export interface LiveQuotesResult {
  source: QuoteSource;
  quotes: Record<string, LiveQuote>;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

// PLANTISTA fallback — callers keep today's retailer/Stripe behavior exactly.
const PLANTISTA_FALLBACK = {
  source: "PLANTISTA" as QuoteSource,
  quotes: {} as Record<string, LiveQuote>,
};

/**
 * Fetches LIVE Shopify prices + availability for the cart's plants.
 *
 * Re-fetches whenever the set of plant ids changes (join of sorted ids is the
 * dep key). On any error or non-OK response it resolves to the PLANTISTA
 * fallback so cart surfaces render exactly as they do today.
 */
export function useLiveQuotes(
  items: CartItem[],
  retailerId?: string
): LiveQuotesResult {
  const [source, setSource] = useState<QuoteSource>("PLANTISTA");
  const [quotes, setQuotes] = useState<Record<string, LiveQuote>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Stable key: sorted plant ids. Changing the set (add/remove) triggers a
  // refetch; quantity changes do not (price is per-unit).
  const plantIds = items.map((it) => it.plant.id);
  const idKey = [...plantIds].sort().join(",");

  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (plantIds.length === 0) {
      setSource("PLANTISTA");
      setQuotes({});
      setLoading(false);
      setError(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(false);

    (async () => {
      try {
        const res = await fetch("/api/commerce/quotes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ retailerId, plantIds }),
          signal: controller.signal,
        });

        if (!res.ok) {
          setSource(PLANTISTA_FALLBACK.source);
          setQuotes(PLANTISTA_FALLBACK.quotes);
          return;
        }

        const data = await res.json();
        setSource(data.source === "SHOPIFY" ? "SHOPIFY" : "PLANTISTA");
        setQuotes(
          data.quotes && typeof data.quotes === "object" ? data.quotes : {}
        );
      } catch (err) {
        // Aborted requests are expected on unmount / id-set change — ignore.
        if ((err as Error)?.name === "AbortError") return;
        setError(true);
        setSource(PLANTISTA_FALLBACK.source);
        setQuotes(PLANTISTA_FALLBACK.quotes);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, retailerId, refreshTick]);

  return { source, quotes, loading, error, refresh };
}
